import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Database, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { applicationsQuery } from '@/features/application/queries'
import { CreateApplicationDialog } from '@/features/application/components/create-application-dialog'
import { CreateSpreadsheetSyncDialog } from '@/features/database/data-sync/create-spreadsheet-sync'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { byOrder } from '@/shared/lib/order'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/shell/database/$databaseId/sync/assets'
const CHOOSE_ROUTE = '/shell/sync/assets'

function NoFile() {
  const { t } = useTranslation()

  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{t('dataSync.assets.title')}</EmptyTitle>
        <EmptyDescription>{t('dataSync.assets.noFile')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}

/**
 * Where Lithos Assets sends somebody who wants a spreadsheet as a table. Assets
 * knows the file but none of the databases, so the person picks the one the
 * table goes into, or makes a new one.
 */
export function SyncAssetsChooseDatabasePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { assetsFileId } = useSearch({ from: CHOOSE_ROUTE })
  const { workspace, isPending } = useTenantWorkspace()
  const applications = useQuery({
    ...applicationsQuery(workspace?.id ?? 0),
    enabled: workspace !== undefined,
  })
  const [creating, setCreating] = useState(false)

  if (assetsFileId === undefined) return <NoFile />

  const open = (databaseId: number) =>
    void navigate({
      to: '/database/$databaseId/sync/assets',
      params: { databaseId },
      search: { assetsFileId },
    })

  if (creating && workspace !== undefined) {
    return (
      <CreateApplicationDialog
        workspaceId={workspace.id}
        type="database"
        open
        onOpenChange={(next) => {
          if (!next) setCreating(false)
        }}
        onCreated={open}
        // The synced table is the one this database is made for.
        initWithData={false}
      />
    )
  }

  // Only reachable for an account the identity provider has not placed in a tenant.
  const noWorkspace = !isPending && workspace === undefined
  const databases = byOrder(applications.data ?? []).filter(
    (application) => application.type === 'database' && application.user_can_access,
  )

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) void navigate({ to: '/' })
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dataSync.assets.title')}</DialogTitle>
          <DialogDescription>
            {noWorkspace
              ? t('workspace.notFoundDescription')
              : t('dataSync.assets.chooseDatabase')}
          </DialogDescription>
        </DialogHeader>

        {noWorkspace ? null : applications.isPending ? (
          <Skeleton className="h-24" />
        ) : (
          <div className="grid gap-2">
            {databases.map((database) => (
              <Button
                key={database.id}
                variant="outline"
                className="justify-start"
                onClick={() => open(database.id)}
              >
                <Database />
                {database.name}
              </Button>
            ))}
            <Button
              variant={databases.length === 0 ? 'default' : 'ghost'}
              className="justify-start"
              onClick={() => setCreating(true)}
            >
              <Plus />
              {t('application.create.database')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Making the synced table in the database chosen, with the file in the address. */
export function SyncAssetsPage() {
  const navigate = useNavigate()
  const { databaseId } = useParams({ from: ROUTE })
  const { assetsFileId } = useSearch({ from: ROUTE })

  if (assetsFileId === undefined) return <NoFile />

  return (
    <CreateSpreadsheetSyncDialog
      databaseId={databaseId}
      fileId={assetsFileId}
      onClose={() => void navigate({ to: '/' })}
      onCreated={(tableId) =>
        void navigate({
          to: '/database/$databaseId/table/$tableId',
          params: { databaseId, tableId },
          replace: true,
        })
      }
    />
  )
}
